import {
  FeatherActivity,
  FeatherAlertTriangle,
  FeatherCheckCircle,
  FeatherDollarSign,
  FeatherFileText,
  FeatherLock,
  FeatherMail,
  FeatherMessageSquare,
  FeatherPackage,
  FeatherRefreshCw,
  FeatherRotateCcw,
  FeatherSend,
  FeatherTrendingUp,
  FeatherUserCheck,
  FeatherUserPlus,
  FeatherUsers,
  FeatherXCircle,
} from '@subframe/core';
import type { ComponentType } from 'react';

import type {
  ApuracaoAuditoriaAcao,
  ApuracaoStatus,
  CategoriaEvento,
  EventoStatus,
  ModalidadePostal,
  MotivoRejeicao,
  ProdutoTipoUso,
  TipoCliente,
} from '../types';

type BadgeVariant = 'success' | 'warning' | 'error' | 'neutral' | 'brand';

interface StatusConfig {
  label: string;
  variant: BadgeVariant;
  icon: ComponentType<{ className?: string }>;
}

export const apuracaoStatusConfig: Record<ApuracaoStatus, StatusConfig> = {
  ABERTO: { label: 'Aberto', variant: 'warning', icon: FeatherActivity },
  FECHADO: { label: 'Fechado', variant: 'success', icon: FeatherLock },
  REABERTO: { label: 'Reaberto', variant: 'brand', icon: FeatherRotateCcw },
};

export const tipoClienteLabel: Record<TipoCliente, string> = {
  AVULSO: 'Avulso',
  CONVENIADO: 'Conveniado',
};

export const tipoClienteVariant: Record<TipoCliente, BadgeVariant> = {
  AVULSO: 'neutral',
  CONVENIADO: 'brand',
};

export const eventoStatusConfig: Record<EventoStatus, StatusConfig> = {
  APURADO: { label: 'Apurado', variant: 'success', icon: FeatherCheckCircle },
  NAO_COBRAVEL: { label: 'Não cobrável', variant: 'neutral', icon: FeatherFileText },
  TRIAL_FREE: { label: 'Trial · liberado', variant: 'brand', icon: FeatherUserCheck },
  TRIAL_CHARGE: { label: 'Trial · R$ 0,00', variant: 'brand', icon: FeatherUserPlus },
  REJEITADO: { label: 'Rejeitado', variant: 'error', icon: FeatherXCircle },
};

export const categoriaEventoLabel: Record<CategoriaEvento, string> = {
  NOTIFICACAO_ENVIADA: 'Notificações enviadas',
  LOTE_NOTIFICACAO_CONCLUIDO: 'Lotes de notificação',
  NEGOCIACAO_COM_VALOR: 'Negociações com valor',
  NEGOCIACAO_SEM_VALOR: 'Negociações sem valor',
  AR_ENVIADO: 'AR enviados',
  REENVIO_POSTAL: 'Reenvios postais',
  SEDEX: 'Envios SEDEX',
  TAXA_SUCESSO: 'Taxa de sucesso',
  CONSUMO_PACOTE: 'Consumo de pacote',
  BASE_MENSAL_HIBRIDO: 'Base mensal (Híbrido)',
};

export const categoriaEventoIcon: Record<CategoriaEvento, ComponentType<{ className?: string }>> = {
  NOTIFICACAO_ENVIADA: FeatherSend,
  LOTE_NOTIFICACAO_CONCLUIDO: FeatherSend,
  NEGOCIACAO_COM_VALOR: FeatherMessageSquare,
  NEGOCIACAO_SEM_VALOR: FeatherMessageSquare,
  AR_ENVIADO: FeatherMail,
  REENVIO_POSTAL: FeatherRefreshCw,
  SEDEX: FeatherPackage,
  TAXA_SUCESSO: FeatherTrendingUp,
  CONSUMO_PACOTE: FeatherPackage,
  BASE_MENSAL_HIBRIDO: FeatherDollarSign,
};

export const motivoRejeicaoLabel: Record<MotivoRejeicao, string> = {
  SEM_CONTRATO: 'Sem contrato ativo',
  CATEGORIA_DESCONHECIDA: 'Categoria desconhecida',
  FORA_PERIODO: 'Fora do período',
  DUPLICADO: 'Evento duplicado',
  PAYLOAD_INVALIDO: 'Payload inválido',
  BLOQUEIO_TRIAL: 'Bloqueado pelo trial',
};

export const motivoRejeicaoVariant: Record<MotivoRejeicao, BadgeVariant> = {
  SEM_CONTRATO: 'error',
  CATEGORIA_DESCONHECIDA: 'error',
  FORA_PERIODO: 'warning',
  DUPLICADO: 'warning',
  PAYLOAD_INVALIDO: 'error',
  BLOQUEIO_TRIAL: 'neutral',
};

export const auditoriaAcaoConfig: Record<ApuracaoAuditoriaAcao, StatusConfig> = {
  CICLO_CRIADO: { label: 'Ciclo criado', variant: 'neutral', icon: FeatherActivity },
  EVENTO_APURADO: { label: 'Evento apurado', variant: 'success', icon: FeatherCheckCircle },
  EVENTO_REJEITADO: { label: 'Evento rejeitado', variant: 'error', icon: FeatherXCircle },
  EVENTO_REPROCESSADO: {
    label: 'Evento reprocessado',
    variant: 'brand',
    icon: FeatherRefreshCw,
  },
  CICLO_FECHADO: { label: 'Ciclo fechado', variant: 'success', icon: FeatherLock },
  CICLO_REABERTO: { label: 'Ciclo reaberto', variant: 'warning', icon: FeatherRotateCcw },
  PACOTE_ALERTA: { label: 'Alerta de pacote', variant: 'warning', icon: FeatherAlertTriangle },
  FATURA_EMITIDA: { label: 'Fatura emitida', variant: 'brand', icon: FeatherDollarSign },
};

export const TipoClienteIcon = FeatherUsers;

const PRODUTO_TIPO_USO_LABEL: Record<ProdutoTipoUso, string> = {
  POR_USO: 'Por uso',
  ASSINATURA: 'Assinatura',
  PACOTE: 'Pacote',
};

const USAGE_TYPE_LABEL: Record<string, string> = {
  PER_USE: 'Por uso',
  SUBSCRIPTION: 'Assinatura',
  PACKAGE: 'Pacote',
};

export function modalidadeContratoLabel(value: string | null): string | null {
  if (!value) return null;
  return USAGE_TYPE_LABEL[value] ?? PRODUTO_TIPO_USO_LABEL[value as ProdutoTipoUso] ?? value;
}

const MODALIDADE_POSTAL_LABEL: Record<ModalidadePostal, string> = {
  CHARGED_SEPARATELY: 'Cobrada separadamente',
  INCLUDED_IN_PACKAGE: 'Inclusa no pacote',
  CUSTOMER_CHOICE_ON_ONBOARDING: 'Escolha do cliente no onboarding',
};

export function modalidadePostalLabel(value: ModalidadePostal | null): string | null {
  if (!value) return null;
  return MODALIDADE_POSTAL_LABEL[value] ?? value;
}
